<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const store = useStore();

const dialogRef = ref(null);
const isCreating = ref(false);
const isGenerating = ref(false);
const unlayerLoaded = ref(false);
const editorReady = ref(false);

// Form fields
const title = ref('');
const subject = ref('');
const selectedInboxId = ref(null);
const selectedLabels = ref([]);
const htmlContent = ref('');

// AI fields
const aiPrompt = ref('');
const aiResponse = ref(null);
const showAIPanel = ref(true);
const showEditor = ref(false);

// Unlayer instance
let emailEditorInstance = null;

// Load Unlayer script from CDN
const loadUnlayer = () => {
  if (window.unlayer) {
    unlayerLoaded.value = true;
    initializeEditor();
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://editor.unlayer.com/embed.js';
  script.onload = () => {
    unlayerLoaded.value = true;
    initializeEditor();
  };
  document.head.appendChild(script);
};

// Initialize Unlayer Editor
const initializeEditor = () => {
  if (!window.unlayer || emailEditorInstance) return;

  emailEditorInstance = window.unlayer.createEditor({
    id: 'email-editor-container',
    projectId: 283242, // Unlayer project ID
    displayMode: 'email',
    appearance: {
      theme: 'light',
      panels: {
        tools: {
          dock: 'left'
        }
      }
    },
    options: {
      features: {
        colorPicker: {
          presets: ['#000000', '#ffd200', '#ffffff', '#1a73e8', '#34a853']
        }
      }
    }
  });

  emailEditorInstance.addEventListener('editor:ready', () => {
    editorReady.value = true;
    console.log('Unlayer editor hazır!');
  });
};

// Get email inboxes
const inboxes = computed(() => {
  return store.getters['inboxes/getInboxes'].filter(
    inbox => inbox.channel_type === 'Channel::Email'
  );
});

// Get labels
const labels = computed(() => store.getters['labels/getLabels']);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  dialogRef.value?.open();
  loadUnlayer();
});

onBeforeUnmount(() => {
  // Cleanup
  if (emailEditorInstance) {
    emailEditorInstance = null;
  }
});

const isFormValid = computed(() => {
  return title.value && subject.value && selectedInboxId.value &&
         selectedLabels.value.length > 0 && htmlContent.value;
});

const generateWithAI = async () => {
  if (!aiPrompt.value.trim()) {
    alert('Lütfen bir istek yazın');
    return;
  }

  isGenerating.value = true;

  try {
    const accountId = store.state.route.params.accountId;
    const res = await fetch(`/api/v1/accounts/${accountId}/brand_setting/generate_email_design`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: aiPrompt.value })
    });

    if (!res.ok) throw new Error('API error');

    const design = await res.json();

    // Form alanlarını doldur
    subject.value = design.subject || 'Yeni Kampanya';
    htmlContent.value = design.html || '';
    title.value = aiPrompt.value.substring(0, 50);

    aiResponse.value = design;

    // AI tasarımını Unlayer'a yükle
    if (editorReady.value && emailEditorInstance) {
      const unlayerDesign = convertAIToUnlayerDesign(design);
      emailEditorInstance.loadDesign(unlayerDesign);
      showEditor.value = true;
    }

    alert('✅ Email tasarımı AI tarafından oluşturuldu! Unlayer editörde düzenleyebilirsiniz.');
  } catch (error) {
    alert('❌ AI tasarım hatası: ' + error.message);
  } finally {
    isGenerating.value = false;
  }
};

// Convert AI response to Unlayer design JSON
const convertAIToUnlayerDesign = (aiDesign) => {
  // Basit bir HTML to Unlayer conversion
  // Gerçek implementasyonda daha gelişmiş olabilir
  return {
    body: {
      rows: [
        {
          cells: [1],
          columns: [
            {
              contents: [
                {
                  type: 'html',
                  values: {
                    html: aiDesign.html || '<p>Email içeriği</p>',
                    containerPadding: '10px'
                  }
                }
              ]
            }
          ]
        }
      ],
      values: {
        backgroundColor: '#ffffff',
        contentWidth: '600px',
        fontFamily: {
          label: 'Arial',
          value: 'arial,helvetica,sans-serif'
        }
      }
    }
  };
};

const toggleEditor = () => {
  showEditor.value = !showEditor.value;
};

const exportFromUnlayer = async () => {
  if (!emailEditorInstance || !editorReady.value) {
    alert('Editor henüz hazır değil');
    return;
  }

  emailEditorInstance.exportHtml(function (data) {
    const { html } = data;
    htmlContent.value = html;
    alert('✅ Email tasarımı Unlayer\'dan alındı!');
  });
};

const createCampaign = async () => {
  if (!isFormValid.value) {
    alert('Lütfen tüm alanları doldurun');
    return;
  }

  // Son kez Unlayer'dan HTML'i al
  if (showEditor.value && editorReady.value && emailEditorInstance) {
    await new Promise((resolve) => {
      emailEditorInstance.exportHtml(function (data) {
        htmlContent.value = data.html;
        resolve();
      });
    });
  }

  isCreating.value = true;

  try {
    const campaignData = {
      title: title.value,
      message: htmlContent.value,
      inbox_id: selectedInboxId.value,
      audience: selectedLabels.value.map(id => ({ type: 'Label', id })),
      scheduled_at: new Date().toISOString(),
    };

    await store.dispatch('campaigns/create', campaignData);
    alert('Email kampanyası oluşturuldu ve gönderim başladı!');
    emit('close');
  } catch (error) {
    alert('Kampanya oluşturulurken hata oluştu: ' + error.message);
  } finally {
    isCreating.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

const quickPrompts = [
  '29 Ekim Cumhuriyet Bayramı kutlama emaili',
  'Yeni öğrenciler için hoşgeldin maili',
  'Kampanya duyurusu: %20 indirim',
  'Aylık bülten - yeni yayınlar'
];

const useQuickPrompt = (prompt) => {
  aiPrompt.value = prompt;
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="AI + Unlayer Email Kampanyası"
    size="large"
    @close="handleClose"
  >
    <div class="ai-email-builder">
      <!-- AI Panel (Sol) -->
      <div class="ai-panel" v-if="showAIPanel">
        <div class="ai-header">
          <span class="ai-icon">🤖</span>
          <h4>AI Email Asistanı</h4>
        </div>

        <div class="ai-chat">
          <div class="prompt-input">
            <textarea
              v-model="aiPrompt"
              rows="3"
              placeholder="Email isteğinizi yazın... (örn: 29 Ekim kutlama emaili hazırla)"
              :disabled="isGenerating"
            ></textarea>
            <button
              @click="generateWithAI"
              :disabled="isGenerating || !aiPrompt.trim()"
              class="generate-btn"
            >
              {{ isGenerating ? '⏳ Oluşturuluyor...' : '✨ AI ile Tasarla' }}
            </button>
          </div>

          <div class="quick-prompts">
            <p class="quick-title">Hızlı Örnekler:</p>
            <div class="prompt-chips">
              <button
                v-for="(prompt, idx) in quickPrompts"
                :key="idx"
                @click="useQuickPrompt(prompt)"
                class="prompt-chip"
              >
                {{ prompt }}
              </button>
            </div>
          </div>

          <div v-if="aiResponse" class="ai-result">
            <div class="result-badge">✅ Tasarım Hazır!</div>
            <p>Konu: <strong>{{ aiResponse.subject }}</strong></p>
            <p class="result-hint">Unlayer editörde düzenleyin ↓</p>
            <button @click="toggleEditor" class="toggle-editor-btn">
              {{ showEditor ? '📝 Editörü Gizle' : '📝 Editörü Aç' }}
            </button>
          </div>

          <div v-if="!unlayerLoaded" class="loading-unlayer">
            ⏳ Unlayer yükleniyor...
          </div>
          <div v-if="unlayerLoaded && !editorReady" class="loading-unlayer">
            ⏳ Editor hazırlanıyor...
          </div>
        </div>
      </div>

      <!-- Form Panel (Sağ) -->
      <div class="form-panel">
        <!-- Unlayer Editor -->
        <div v-if="showEditor" class="editor-section">
          <div class="editor-header">
            <h4>📧 Email Düzenleyici (Unlayer)</h4>
            <button @click="exportFromUnlayer" class="export-btn">
              💾 Tasarımı Kaydet
            </button>
          </div>
          <div id="email-editor-container" class="unlayer-editor"></div>
        </div>

        <div class="form-section">
          <h4>Kampanya Bilgileri</h4>
          <div class="form-row">
            <label>Kampanya Adı</label>
            <input v-model="title" type="text" placeholder="Örn: Ocak Ayı Kampanyası" />
          </div>
          <div class="form-row">
            <label>Email Konusu</label>
            <input v-model="subject" type="text" placeholder="Email konu satırı" />
          </div>
        </div>

        <div class="form-section">
          <h4>Email Inbox</h4>
          <select v-model="selectedInboxId" class="inbox-select">
            <option :value="null">Seçiniz...</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <div class="form-section">
          <h4>Hedef Etiketler</h4>
          <div class="labels-grid">
            <label
              v-for="label in labels"
              :key="label.id"
              class="label-checkbox"
              :style="{ borderColor: label.color }"
            >
              <input type="checkbox" :value="label.id" v-model="selectedLabels" />
              <span :style="{ color: label.color }">{{ label.title }}</span>
            </label>
          </div>
          <p v-if="labels.length === 0" class="no-labels">Henüz etiket yok</p>
          <p v-else class="selected-count">{{ selectedLabels.length }} etiket seçildi</p>
        </div>

        <div class="form-section" v-if="htmlContent && !showEditor">
          <h4>Email Önizleme</h4>
          <div class="html-preview" v-html="htmlContent"></div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <Button variant="secondary" @click="handleClose">İptal</Button>
        <Button
          variant="primary"
          :disabled="!isFormValid || isCreating"
          :loading="isCreating"
          @click="createCampaign"
        >
          {{ isCreating ? 'Gönderiliyor...' : 'Kampanyayı Gönder' }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.ai-email-builder {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 20px;
  padding: 16px;
  max-height: 75vh;
  overflow: hidden;
}

.ai-panel {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

.ai-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ai-icon {
  font-size: 24px;
}

.ai-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.ai-chat {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.prompt-input {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.prompt-input textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 13px;
  resize: none;
}

.generate-btn {
  padding: 12px 20px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.quick-prompts {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
}

.quick-title {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.prompt-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prompt-chip {
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: left;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.prompt-chip:hover {
  background: #e0f2fe;
  border-color: #93c5fd;
}

.ai-result {
  background: white;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid #22c55e;
}

.result-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #22c55e;
  color: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
}

.result-hint {
  margin: 8px 0;
  font-size: 12px;
  color: #64748b;
}

.toggle-editor-btn {
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.loading-unlayer {
  padding: 12px;
  background: white;
  border-radius: 8px;
  text-align: center;
  font-size: 12px;
  color: #64748b;
}

.form-panel {
  overflow-y: auto;
  padding-right: 8px;
}

.editor-section {
  margin-bottom: 20px;
  border: 2px solid #3b82f6;
  border-radius: 12px;
  overflow: hidden;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
}

.editor-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.export-btn {
  padding: 6px 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.unlayer-editor {
  height: 500px;
  background: white;
}

.form-section {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.form-section h4 {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.form-row {
  margin-bottom: 12px;
}

.form-row label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
}

.form-row input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.inbox-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.labels-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 2px solid;
  border-radius: 16px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.label-checkbox:hover {
  opacity: 0.8;
}

.label-checkbox input {
  width: auto;
  margin: 0;
}

.selected-count, .no-labels {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.html-preview {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: white;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}
</style>
